import alertTokens from "@amzn/meridian-tokens/base/icon/alert";
import checkCircleTokens from "@amzn/meridian-tokens/base/icon/check-circle";
import {
  FILE_TYPE_COLORS,
  FILE_TYPE_LABELS,
  FILE_TYPES,
  formatCount,
  isCurrentDocument,
  regionShortLabel,
  uncoveredDocumentCount,
} from "../adminReportUtils";
import { PolicyDocument, RegionCode } from "../types";
import Chip from "./chip";

interface DocumentsSummaryProps {
  /** Every searchable document in the region, before any filter is applied. */
  documents: PolicyDocument[];
  region: RegionCode;
}

/**
 * The region at a glance: how many searchable documents it has, how they split
 * by file type, and how much of it automation actually covers. Counted over the
 * whole region rather than the filtered rows, so the figures hold still while
 * the table below is narrowed. The swatch colours match the Overview charts.
 *
 * Every figure here is counted over the same set, the documents the index still
 * serves, so the total always equals covered plus uncovered.
 */
const DocumentsSummary = ({ documents, region }: DocumentsSummaryProps) => {
  const currentDocuments = documents.filter(isCurrentDocument);

  const byFileType = FILE_TYPES.map((fileType) => ({
    fileType,
    count: currentDocuments.filter((document) => document.fileType === fileType)
      .length,
  })).filter(({ count }) => count > 0);

  const uncovered = uncoveredDocumentCount(documents);
  const covered = currentDocuments.length - uncovered;

  return (
    <div className="admin-report__summary">
      <div className="admin-report__summary-total">
        <span className="admin-report__summary-count">
          {formatCount(currentDocuments.length)}
        </span>
        <span className="admin-report__summary-label">
          {`searchable document${currentDocuments.length === 1 ? "" : "s"} in ${regionShortLabel(region)}`}
        </span>
      </div>

      <div className="admin-report__summary-types">
        {byFileType.map(({ fileType, count }) => (
          <span key={fileType} className="admin-report__summary-type">
            <span
              className="admin-report__summary-swatch"
              style={{ backgroundColor: FILE_TYPE_COLORS[fileType] }}
            />
            {FILE_TYPE_LABELS[fileType]}
            <span className="admin-report__summary-type-value">
              {formatCount(count)}
            </span>
          </span>
        ))}
      </div>

      <div className="admin-report__chips">
        <Chip tone="green" tokens={checkCircleTokens}>
          {`${formatCount(covered)} covered`}
        </Chip>
        {uncovered > 0 && (
          <Chip tone="red" tokens={alertTokens}>
            {`${formatCount(uncovered)} uncovered`}
          </Chip>
        )}
      </div>
    </div>
  );
};

export default DocumentsSummary;
